import { useIntl } from '@/intl'
import { getRoomsResponseSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import {
  Button,
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const Wrapper = styled(SpaceBetween)`
  background: ${tk('color_semantic_bg_subdued')};
  padding: ${tk('spacing_md')};
  align-items: start;
`

const ContentWrapper = styled(Collapsible)`
  width: 100%;
`

const AgeProfilesWrapper = styled(SpaceBetween)`
  max-width: 640px;
`

const CollapseButton = styled(Button)`
  padding: 0;
  margin-bottom: ${tk('spacing_xs')};

  min-height: unset;

  text-decoration: underline;
  color: ${tk('color_semantic_text_strong')};
`

const AgeProfileWrapper = styled.div`
  display: flex;
`

export function AgeProfilesNew() {
  const intl = useIntl()
  const { specialAgeProfiles } = usePricesSelector(getRoomsResponseSelector)
  const readLess = React.useMemo(() => intl.formatMessage({ id: 'prices_room_selector_read_less' }), [intl])
  const readMore = React.useMemo(() => intl.formatMessage({ id: 'prices_room_selector_read_more' }), [intl])
  const [toggleText, setToggleText] = React.useState(readMore)

  const onChangeCollapsible = React.useCallback(
    (open: boolean) => {
      setToggleText(open ? readLess : readMore)
    },
    [readLess, readMore],
  )

  if (!specialAgeProfiles || isEmpty(specialAgeProfiles))
    return null

  const translationIds: Record<string, string> = {
    adults: 'quicksearch_filter_participants_adults',
    children: 'quicksearch_filter_participants_children',
    babies: 'quicksearch_filter_participants_babies',
  }

  return (
    <CollapsibleRoot onOpenChange={onChangeCollapsible}>
      <Wrapper direction="y" spacing={tk('spacing_sm')}>
        <Typography $variant="label">
          {intl.formatMessage({ id: 'prices_room_selector_age_profiles_title' })}
        </Typography>
        <ContentWrapper>
          <AgeProfilesWrapper direction="y" spacing={tk('spacing_md')}>
            <Typography $variant="body-md">
              {intl.formatMessage({ id: 'prices_room_selector_age_profiles_description' })}
            </Typography>
            <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
              {Object.entries(specialAgeProfiles).map(([ageProfile, ageRange]) => (
                <AgeProfileWrapper key={ageProfile}>
                  <Typography $variant="label">{intl.formatMessage({ id: translationIds[ageProfile] })}</Typography>
                  <Typography $variant="body-md">
                    {intl.formatMessage(
                      { id: 'prices_room_selector_age_profiles_range_description' },
                      { minAge: ageRange?.minAge, maxAge: ageRange?.maxAge },
                    )}
                  </Typography>
                </AgeProfileWrapper>
              ))}
            </SpaceBetween>
          </AgeProfilesWrapper>
        </ContentWrapper>
        <CollapsibleTrigger asChild>
          <CollapseButton $hierarchy="tertiary" $withoutColor>
            {toggleText}
          </CollapseButton>
        </CollapsibleTrigger>
      </Wrapper>
    </CollapsibleRoot>
  )
}
